import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';
import PhoneInput from '../components/PhoneInput';

const FILTER_MODES = {
  ALL: 'all',
  POPULAR: 'popular',
};

export default function PreorderPage() {
  const currencyCode = useCurrencyCode();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterMode, setFilterMode] = useState(FILTER_MODES.ALL);
  const [cart, setCart] = useState([]);
  const [note, setNote] = useState('');
  const [deliveryOption, setDeliveryOption] = useState('delivery');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [customerInfoExpanded, setCustomerInfoExpanded] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerWhatsApp, setCustomerWhatsApp] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [showCategoryFilters, setShowCategoryFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [noteExpanded, setNoteExpanded] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/products');
      setProducts(res.data || []);
    } catch (err) {
      console.error('Failed to load preorder products', err);
      toast.error('Unable to load preorder products.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data || []);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const filteredProducts = useMemo(() => {
    const base =
      filterMode === FILTER_MODES.POPULAR
        ? products.filter((product) => product.isPopular)
        : products;
    const categoryId = Number(selectedCategoryId);
    let results =
      categoryId && categoryId > 0
        ? base.filter((product) => product.categoryId === categoryId)
        : base;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (normalizedSearch) {
      results = results.filter((product) => {
        const nameMatch = product.name?.toLowerCase().includes(normalizedSearch);
        const skuMatch = product.sku?.toLowerCase().includes(normalizedSearch);
        return nameMatch || skuMatch;
      });
    }
    return results;
  }, [filterMode, products, selectedCategoryId, searchTerm]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    products.forEach((product) => {
      if (!product.categoryId) return;
      counts[product.categoryId] = (counts[product.categoryId] || 0) + 1;
    });
    return counts;
  }, [products]);

  const handleAddToCart = (product) => {
    setSuccessMessage('');
    setCart((prev) => {
      const existing = prev.find((item) => item.productId === product.id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [
        ...prev,
        {
          productId: product.id,
          name: product.name,
          price: Number(product.priceInclTax ?? product.price ?? 0),
          quantity: 1,
        },
      ];
    });
  };

  const handleQuantityChange = (productId, nextQuantity) => {
    setSuccessMessage('');
    setCart((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, quantity: Math.max(1, nextQuantity) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const handleRemoveItem = (productId) => {
    setSuccessMessage('');
    setCart((prev) => prev.filter((item) => item.productId !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleSubmit = async () => {
    if (cart.length === 0) {
      toast.error('Please add at least one product to your pre-order.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/staff/preorders', {
        products: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
        note: note.trim() || null,
        deliveryOption,
        source: 'customer',
        customerName: customerName.trim() || null,
        customerWhatsApp: customerWhatsApp.trim() || null,
        customerEmail: customerEmail.trim() || null,
        customerAddress: customerAddress.trim() || null,
      });
      setSuccessMessage(
        'Thanks! We have received your pre-order and will confirm availability shortly.'
      );
      setCart([]);
      setNote('');
      setNoteExpanded(false);
      setDeliveryOption('delivery');
      setCustomerName('');
      setCustomerWhatsApp('');
      setCustomerEmail('');
      setCustomerAddress('');
      setCustomerInfoExpanded(false);
      toast.success('Pre-order submitted.');
    } catch (err) {
      console.error('Failed to submit pre-order', err);
      toast.error(err.response?.data?.error || 'Unable to submit your pre-order.');
    } finally {
      setSubmitting(false);
    }
  };

  const isPopularActive = filterMode === FILTER_MODES.POPULAR;
  const sectionTitle = isPopularActive ? 'Popular items' : 'All preorder items';

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-hidden bg-slate-950 px-6 py-6 text-white">
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Pre-order</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Pre-order items</h1>
            <p className="text-sm text-slate-400">
              Choose from the curated selection, mark anything extra, and pick delivery or collection.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFilterMode(FILTER_MODES.ALL)}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] transition ${
                isPopularActive
                  ? 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500'
                  : 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setFilterMode(FILTER_MODES.POPULAR)}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] transition ${
                isPopularActive
                  ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                  : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500'
              }`}
            >
              Popular
            </button>
          </div>
        </div>
        {successMessage && (
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-900/20 px-4 py-3 text-sm text-emerald-200">
            {successMessage}
          </div>
        )}
      </header>

      <div className="flex flex-1 flex-col gap-6 lg:flex-row min-h-0">
        <section
          className="flex flex-1 flex-col min-h-0 overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/70 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]"
        >
          <div className="border-b border-slate-800 px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-[0.3em] text-slate-500">
                  {sectionTitle}
                </span>
                <span className="text-xs text-slate-400">{filteredProducts.length} products</span>
              </div>
              <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-end lg:flex-none">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search the menu..."
                  aria-label="Search preorder items"
                  className="flex-1 rounded-full border border-slate-800 bg-slate-950/70 px-4 py-2 text-sm text-white placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                <button
                  type="button"
                  onClick={() => setShowCategoryFilters((prev) => !prev)}
                  className="rounded-full border border-slate-800 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-200 transition hover:border-emerald-400 sm:ml-2"
                  aria-expanded={showCategoryFilters}
                >
                  {showCategoryFilters ? 'Hide categories' : 'Show categories'}
                </button>
              </div>
            </div>
          </div>
          {showCategoryFilters && (
            <div className="border-b border-slate-800 px-4 py-3">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                <CategoryCard
                  name="All products"
                  count={products.length}
                  selected={!selectedCategoryId}
                  onClick={() => setSelectedCategoryId(null)}
                />
                {categories.map((category) => (
                  <CategoryCard
                    key={category.id}
                    name={category.name}
                    count={categoryCounts[category.id] || 0}
                    selected={Number(selectedCategoryId) === Number(category.id)}
                    onClick={() =>
                      setSelectedCategoryId((prev) =>
                        Number(prev) === Number(category.id) ? null : category.id
                      )
                    }
                  />
                ))}
              </div>
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {loading ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                Loading items...
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                {filterMode === FILTER_MODES.POPULAR
                  ? 'No popular items available right now.'
                  : 'No preorder items are available.'}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                {filteredProducts.map((product) => {
                  const price = Number(product.priceInclTax ?? product.price ?? 0) || 0;
                  return (
                    <button
                      key={product.id}
                      onClick={() => handleAddToCart(product)}
                      type="button"
                      className="group flex h-36 flex-col justify-between rounded-2xl border border-slate-800 bg-slate-900/80 p-4 text-left transition hover:border-emerald-400 hover:bg-slate-800"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-white group-hover:text-emerald-300">
                            {product.name}
                          </p>
                          {product.isPopular && (
                            <span className="rounded-full border border-emerald-500/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-200">
                              Popular
                            </span>
                          )}
                        </div>
                        <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
                          {product.sku || 'SKU n/a'}
                        </p>
                      </div>
                      <div className="text-right text-lg font-semibold text-white">
                        {formatCurrency(price, currencyCode)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="flex w-full flex-col min-h-0 overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/70 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)] lg:w-[32%]">
          <div className="border-b border-slate-800 px-4 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Your cart</h2>
              <span className="text-xs text-slate-400">
                {totalItems} item{totalItems === 1 ? '' : 's'}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-slate-400">
              <span>Customer info (optional)</span>
              <button
                type="button"
                onClick={() => setCustomerInfoExpanded((prev) => !prev)}
                className="text-[10px] font-semibold uppercase tracking-[0.4em] text-emerald-300 transition hover:text-emerald-200"
                aria-expanded={customerInfoExpanded}
              >
                {customerInfoExpanded ? 'Hide' : 'Add'}
              </button>
            </div>
            {customerInfoExpanded && (
              <div className="space-y-3">
                <input
                  type="text"
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                  placeholder="Name"
                  className="w-full rounded-2xl border border-slate-800 bg-slate-950/80 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                />
                <PhoneInput
                  value={customerWhatsApp}
                  onChange={(value) => setCustomerWhatsApp(value)}
                  label="WhatsApp number"
                  placeholder="WhatsApp number"
                  wrapperClassName="mt-1"
                  showLabel={false}
                />
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(event) => setCustomerEmail(event.target.value)}
                  placeholder="Email address"
                  className="w-full rounded-2xl border border-slate-800 bg-slate-950/80 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                />
                <input
                  type="text"
                  value={customerAddress}
                  onChange={(event) => setCustomerAddress(event.target.value)}
                  placeholder="Delivery address (optional)"
                  className="w-full rounded-2xl border border-slate-800 bg-slate-950/80 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                />
              </div>
            )}
          </div>
          <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-auto p-4">
            {cart.length === 0 ? (
              <div className="text-sm text-slate-400">
                Tap a product to add it to your preorder cart.
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => (
                  <div
                    key={item.productId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/70 px-4 py-3 text-sm"
                  >
                    <div>
                      <p className="font-semibold text-white">{item.name}</p>
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                        {formatCurrency(item.price, currencyCode)} each
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Qty
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(event) =>
                          handleQuantityChange(item.productId, Number(event.target.value) || 1)
                        }
                        className="w-12 rounded-xl border border-slate-700 bg-slate-900 px-2 py-1 text-center text-xs text-slate-100 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item.productId)}
                        className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-400 transition hover:text-rose-200"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-3 border-t border-slate-800 bg-slate-900/80 px-4 py-4">
            <div className="flex items-center justify-between text-sm text-slate-400">
              <span>Total</span>
              <span className="text-base font-semibold text-white">
                {formatCurrency(cartTotal, currencyCode)}
              </span>
            </div>

            <div className="space-y-2">
              {noteExpanded && (
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows="3"
                  className="w-full rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="List additional items or special instructions..."
                />
              )}
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-slate-500">
                <span>Delivery option</span>
                <button
                  type="button"
                  onClick={() => setNoteExpanded((prev) => !prev)}
                  className="rounded-full border border-slate-800 px-4 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-200 transition hover:border-emerald-400"
                >
                  {noteExpanded ? 'Hide note' : 'Add note'}
                </button>
              </div>
              <div className="flex gap-3">
                {['delivery', 'collection'].map((option) => (
                  <label
                    key={option}
                    className={`flex flex-1 cursor-pointer items-center justify-center rounded-2xl border px-4 py-2 text-sm font-semibold uppercase tracking-[0.3em] transition ${
                      deliveryOption === option
                        ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                        : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-emerald-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="deliveryOption"
                      value={option}
                      checked={deliveryOption === option}
                      onChange={() => setDeliveryOption(option)}
                      className="hidden"
                    />
                    {option === 'delivery' ? 'Delivery' : 'Collection'}
                  </label>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || cart.length === 0}
              className="w-full rounded-2xl bg-emerald-500 px-4 py-3 text-center text-sm font-semibold uppercase tracking-[0.3em] text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Submitting...' : 'Submit Pre-order'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function CategoryCard({ name, count, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col justify-between rounded-2xl border px-4 py-3 text-left transition ${
        selected
          ? 'border-emerald-400 bg-emerald-500/10 text-emerald-200 shadow-lg shadow-emerald-500/20'
          : 'border-slate-800 bg-slate-900 text-slate-300 hover:border-emerald-400'
      }`}
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-slate-400">
        <span className="text-slate-400">Browse</span>
        <span className="text-[10px] text-slate-400">
          {count} product{count === 1 ? '' : 's'}
        </span>
      </div>
      <p className="text-sm font-semibold leading-tight text-white">{name}</p>
    </button>
  );
}
